import { fabric } from 'fabric';
import { GraphType, GraphName } from '../constants/Constants';
import log from 'loglevel';
import { generateUUID, getObjectByUUID, setObjectCanSelect } from '../utils/utils';
import CanvasBitmap from './CanvasBitmap';
import CanvasCircle from './CanvasCircle';
import CanvasDoubleArrow from './CanvasDoubleArrow';
import CanvasIsoscelesTriangle from './CanvasIsoscelesTriangle';
import CanvasLine from './CanvasLine';
import CanvasNitePen from './CanvasNitePen';
import CanvasPen from './CanvasPen';
import CanvasRightTriangle from './CanvasRightTriangle';
import CanvasSingleArrow from './CanvasSingleArrow';
import CanvasSquare from './CanvasSquare';
import CanvasText from './CanvasText';
import Graphic from './Graphic';

/**
 *
 *
 * @export
 * @class GraphicFactory
 * @author zhenliang.sun
 * @date 2018-05-03 14:50:03
 */
export default class GraphicFactory {
  constructor(app) {
    this.app = app;
  }

  get tools() {
    return this.app.tools;
  }

  get stage() {
    return this.app.stage;
  }

  get root() {
    return this.app.canvas;
  }

  get store() {
    return this.app.store;
  }

  /**
   * 根据图形类别创建相关的图形
   *
   * @param {any} type
   * @returns
   * @memberof GraphicFactory
   */
  createGraphic(type) {
    let root = this.app.canvas;
    setObjectCanSelect(root, false);
    switch (type) {
      case GraphType.PEN:
        let pen = new CanvasPen(this.app);
        pen.app = this.app;
        return pen;
      case GraphType.NITE_WHITE_PEN:
        let nitePen = new CanvasNitePen(this.app);
        nitePen.app = this.app;
        return nitePen;
      case GraphType.SQUARE:
        let square = new CanvasSquare(this.app);
        square.app = this.app;
        return square;
      case GraphType.CIRCLE:
        let circle = new CanvasCircle(this.app);
        circle.app = this.app;
        return circle;
      case GraphType.ARROW:
        break;
      case GraphType.BITMAP:
        setObjectCanSelect(root, true);
        let bitmap = new CanvasBitmap(this.app);
        bitmap.app = this.app;
        return bitmap;
      case GraphType.TEXT:
        let text = new CanvasText(this.app);
        text.app = this.app;
        return text;
      case GraphType.RIGHT_TRIANGLE:
        let rt = new CanvasRightTriangle(this.app);
        rt.app = this.app;
        return rt;
      case GraphType.ISOSCELES_TRIANGLE:
        let it = new CanvasIsoscelesTriangle(this.app);
        it.app = this.app;
        return it;
      case GraphType.LINE:
        let line = new CanvasLine(this.app);
        line.app = this.app;
        return line;
      case GraphType.SINGLE_ARROW:
        let sa = new CanvasSingleArrow(this.app);
        sa.app = this.app;
        return sa;
      case GraphType.DOUBLE_ARROW:
        let da = new CanvasDoubleArrow(this.app);
        da.app = this.app;
        return da;
      default:
        setObjectCanSelect(root, this.store.getKV('mouseEnable'));
        return null;
    }
  }

  createAddData(target) {
    let result;
    if (!target) {
      return result;
    }
    target.uuid = generateUUID();
    // 因为创建只能是一个工具命令
    let toolId = this.tools.currentGraphicId;
    target.gtype = toolId;
    target.pageId = this.store.getKV('pageId');
    let opt = this.tools.defaultControlOption;
    Object.assign(target, opt);
    switch (toolId) {
      case GraphType.PEN:
        result = CanvasPen.toAddData(target);
        break;
      case GraphType.NITE_WHITE_PEN:
        result = CanvasNitePen.toAddData(target);
        break;
      case GraphType.SQUARE:
        result = CanvasSquare.toAddData(target);
        break;
      case GraphType.CIRCLE:
        result = CanvasCircle.toAddData(target);
        break;
      case GraphType.ARROW:
        break;
      case GraphType.BITMAP:
        result = CanvasBitmap.toAddData(target);
        break;
      case GraphType.TEXT:
        result = CanvasText.toAddData(target);
        break;
      case GraphType.RIGHT_TRIANGLE:
        result = CanvasRightTriangle.toAddData(target);
        break;
      case GraphType.ISOSCELES_TRIANGLE:
        result = CanvasIsoscelesTriangle.toAddData(target);
        break;
      case GraphType.LINE:
        result = CanvasLine.toAddData(target);
        break;
      case GraphType.SINGLE_ARROW:
        result = CanvasSingleArrow.toAddData(target);
        break;
      case GraphType.DOUBLE_ARROW:
        result = CanvasDoubleArrow.toAddData(target);
        break;
      default:
        return null;
    }

    result.cw = this.store.getKV('width') || this.store.getKV('ow');
    result.ch = this.store.getKV('height') || this.store.getKV('oh');
    result.pageId = this.store.getKV('pageId');
    return result;
  }

  createModifyData(target) {
    let result;
    switch (target.gtype) {
      case GraphType.PEN:
      case GraphType.SQUARE:
      case GraphType.CIRCLE:
      case GraphType.BITMAP:
      case GraphType.ISOSCELES_TRIANGLE:
      case GraphType.RIGHT_TRIANGLE:
      case GraphType.SINGLE_ARROW:
      case GraphType.DOUBLE_ARROW:
        result = Graphic.toModifyData(target);
        break;
      case GraphType.TEXT:
        result = CanvasText.toModifyData(target);
        break;
      case GraphType.ARROW:
        break;
      case GraphType.NITE_WHITE_PEN:
        // 因为会自动消失，所以应该没人手速那么快再次操作其形状吧。
        break;
      default:
        return null;
    }

    if (result) {
      result.pageId = this.store.getKV('pageId');
    }

    return result;
  }

  createRemoveData(target) {
    if (!target || !target.uuid) {
      return null;
    }
    let result = {};
    result.uuid = target.uuid;
    result.uniqueID = Date.now();
    result.pageId = this.store.getKV('pageId');
    return result;
  }

  createByData(data) {
    var mergeData = {
      strokeLineCap: 'round',
      strokeLineJoin: 'round'
    };
    let gtype = data.gtype;

    let result;
    switch (gtype) {
      case GraphType.PEN:
        // var penStart = 'M, ' + data.path.shift().toString() + ',L ';
        // var penLast = data.path.toString();
        // result = new fabric[type](penStart + penLast, {

        // 如果只是一个点的话，给的宽高是0， 但是这样接受数据的时候，画到舞台上， 就不显示了。 所以基于最小笔触大小，设置该点的宽高
        data.width = data.width === 0 ? data.strokeWidth : data.width;
        data.height = data.height === 0 ? data.strokeWidth : data.height;
        result = new fabric[GraphName.PEN](data.path, {
          fill: '',
          stroke: data.stroke,
          strokeWidth: data.strokeWidth,
          strokeLineCap: 'round',
          strokeLineJoin: 'round'
        });
        result.uuid = data.uuid;
        result.gtype = gtype;
        break;
      case GraphType.SQUARE:
        result = new fabric[GraphName.SQUARE](data);
        break;
      case GraphType.NITE_WHITE_PEN:
        result = new fabric[GraphName.NITE_WHITE_PEN](data.path, {
          fill: '',
          stroke: data.stroke,
          strokeWidth: data.strokeWidth,
          strokeLineCap: 'round',
          strokeLineJoin: 'round'
        });
        result.uuid = data.uuid;
        result.gtype = gtype;
        result.shadow = CanvasNitePen.createShadow(data.blurStroke);
        result.animate('opacity', 0, {
          duration: 1000,
          onChange: this.stage.renderNow.bind(this.stage),
          onComplete: () => {
            this.root.remove(result);
          }
        });
        // CanvasNitePen.drawFromData(result, data)
        break;
      case GraphType.CIRCLE:
        result = new fabric[GraphName.CIRCLE](data);
        break;
      case GraphType.DOUBLE_ARROW:
        result = new fabric[GraphName.DOUBLE_ARROW](data.path, Object.assign({}, data, mergeData));
        break;
      case GraphType.SINGLE_ARROW:
        result = new fabric[GraphName.SINGLE_ARROW](data.path, Object.assign({}, data, mergeData));
        break;
      case GraphType.ISOSCELES_TRIANGLE:
        result = new fabric[GraphName.TRIANGLE](Object.assign({}, data, mergeData));
        break;
      case GraphType.RIGHT_TRIANGLE:
        result = new fabric[GraphName.RIGHT_TRIANGLE](data.path, Object.assign({}, data, mergeData));
        break;
      case GraphType.BITMAP:
        /* eslint-disable no-undef */
        CANVAS_BITMAP.add(data.url);
        break;
      case GraphType.TEXT:
        result = new fabric[GraphName.TEXT]('', Object.assign({}, data, mergeData));
        result.editable = this.store.getKV('mouseEnable');
        break;
      case GraphType.LINE:
        result = new fabric[GraphName.LINE](Object.assign({}, data, mergeData));
        break;
      default:
        break;
    }
    if (result) {
      result.set(data);
      result.selectable = this.store.getKV('mouseEnable');
      // 设置选中样式
      Object.assign(result, this.tools.defaultControlOption);
    }

    return result;
  }

  modifyByData(data) {
    if (!data || !data.uuid) {
      log.error('modify, data format error ', data);
      return;
    }
    let uuid = data.uuid;
    let root = this.app.canvas;
    let obj = getObjectByUUID(root, uuid);
    if (!obj) {
      log.warn(`can't find object uuid ${uuid}`);
      return;
    }

    // 防止循环设置scaleX = -1 导致 原件不停翻转
    if (obj.flipX) {
      data.scaleX *= -1;
    }

    if (obj.flipY) {
      data.scaleY *= -1;
    }
    obj.set(data); // item, data[item]//{ angle: Math.random() * 360, stroke: "#" + (parseInt(Math.random() * 0xffffff)).toString(16), strokeWidth: Math.random() * 10 + 10, scaleX: Math.random() * 2 + 1, scaleY: Math.random() * 2 + 1 }
    // 设置完属性过后，重新计算一下舞台坐标
    obj.setCoords();
    this.stage.renderNow();
  }

  removeByData(data) {
    if (!data || !data.uuid) {
      log.error('remove, data format error ', data);
      return;
    }
    let uuid = data.uuid;
    let root = this.app.canvas;
    let obj = getObjectByUUID(root, uuid);
    if (!obj) {
      log.warn(`doesn't find object uuid ${uuid}`);
      return;
    }

    root.remove(obj);
    this.stage.renderNow();
  }
}
